<template>
  <div class="col-lg-12 col-md-12 col-sm-12" v-if="hasData">
    <div class="card mb-3">
      <div class="header">Mandible Growth Curve (Gompertz Fit)</div>

      <div class="body mndmxl-layout">
        <div class="chart-container maxillary-chart-wrapper">
          <v-chart
            ref="mandibleChart"
            class="chart"
            :option="mandibleOption"
            autoresize
          />

          <div class="mandmxl-video-overlay">
            <video autoplay loop muted playsinline>
              <source src="@/assets/mandible.mp4" type="video/mp4" />
            </video>
          </div>
        </div>
      </div>
    </div>

    <div class="card">
      <div class="header">Maxillary Growth Curve (Gompertz Fit)</div>
      <div class="body mndmxl-layout">
        <!-- Chart -->
        <div class="chart-container maxillary-chart-wrapper">
          <v-chart
            ref="maxillaryChart"
            class="chart"
            :option="maxillaryOption"
            autoresize
          />
          <div class="mandmxl-video-overlay">
            <video autoplay loop muted playsinline>
              <source src="@/assets/maxillary.mp4" type="video/mp4" />
            </video>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import VChart from "vue-echarts";
import crossUrl from "@/assets/cross.png";

export default {
  name: "MandibleMaxillaryChart",
  components: {
    "v-chart": VChart,
  },
  props: {
    chartData: { type: Object, required: true },
    patientAge: { type: Number, required: true },
    subjectMandibleArc: { type: Number, required: true },
    subjectMaxillaryArc: { type: Number, required: true },
  },
  data() {
    return {
      mandibleOption: null,
      maxillaryOption: null,
    };
  },
  computed: {
    hasData() {
      return (
        this.chartData && this.chartData.mandible && this.chartData.maxillary
      );
    },
  },
  mounted() {
    this.buildCharts();
  },
  watch: {
    chartData: {
      deep: true,
      immediate: true,
      handler() {
        this.buildCharts();
      },
    },
    patientAge() {
      this.buildCharts();
    },
    subjectMandibleArc() {
      this.buildCharts();
    },
    subjectMaxillaryArc() {
      this.buildCharts();
    },
  },
  methods: {
    erf(x) {
      const sign = x >= 0 ? 1 : -1;
      x = Math.abs(x);

      const a1 = 0.254829592;
      const a2 = -0.284496736;
      const a3 = 1.421413741;
      const a4 = -1.453152027;
      const a5 = 1.061405429;
      const p = 0.3275911;

      const t = 1 / (1 + p * x);
      const y =
        1 -
        ((((a5 * t + a4) * t + a3) * t + a2) * t + a1) * t * Math.exp(-x * x);
      return sign * y;
    },

    zToPercentile(z) {
      return 0.5 * (1 + this.erf(z / Math.sqrt(2))) * 100;
    },

    interpolate(xs, ys, x) {
      if (
        !Array.isArray(xs) ||
        !Array.isArray(ys) ||
        xs.length < 2 ||
        ys.length < 2
      )
        return null;

      const n = Math.min(xs.length, ys.length);
      const xVals = xs.slice(0, n);
      const yVals = ys.slice(0, n);

      if (x <= xVals[0]) return yVals[0];
      if (x >= xVals[n - 1]) return yVals[n - 1];

      let i = 0;
      while (i < n - 1 && xVals[i + 1] < x) i++;

      const x0 = xVals[i];
      const x1 = xVals[i + 1];
      const y0 = yVals[i];
      const y1 = yVals[i + 1];

      if (x1 === x0) return y0;

      const t = (x - x0) / (x1 - x0);
      return y0 + t * (y1 - y0);
    },

    alignedSeries(xs, ys) {
      const n = Math.min(xs?.length || 0, ys?.length || 0);
      return Array.from({ length: n }, (_, i) => [xs[i], ys[i]]);
    },

    computeArcStats(xVals, fit, p10, p90, patientAge, subjectValue) {
      const mean = this.interpolate(xVals, fit, patientAge);
      const low = this.interpolate(xVals, p10, patientAge);
      const high = this.interpolate(xVals, p90, patientAge);

      if (mean == null || low == null || high == null) {
        return { zScore: null, percentile: null };
      }

      const sigma = (high - low) / 2.5631; // exact normal-width conversion
      if (!sigma || sigma <= 0) {
        return { zScore: null, percentile: null };
      }

      const zScore = (subjectValue - mean) / sigma;
      const percentile = this.zToPercentile(zScore);

      return { zScore, percentile, mean, sigma };
    },

    buildCharts() {
      if (!this.hasData) return;

      const m = this.chartData.mandible;
      const x = this.chartData.maxillary;

      const mandibleX = m.fit_curve_x || m.ages || [];
      const maxillaryX = x.fit_curve_x || x.ages || [];

      const mandibleStats = this.computeArcStats(
        mandibleX,
        m.fit || [],
        m.p10 || [],
        m.p90 || [],
        this.patientAge,
        this.subjectMandibleArc,
      );

      const maxillaryStats = this.computeArcStats(
        maxillaryX,
        x.fit || [],
        x.p10 || [],
        x.p90 || [],
        this.patientAge,
        this.subjectMaxillaryArc,
      );

      this.$emit("arcs_zpercentile", {
        mandible: { value: this.subjectMandibleArc, ...mandibleStats },
        maxillary: { value: this.subjectMaxillaryArc, ...maxillaryStats },
      });

      this.mandibleOption = this.buildOption(
        "Mandible Arc (mm)",
        this.chartData.mandible,
        mandibleStats,
        "#4e79a7",
        "Subject Mandible Arc",
        this.subjectMandibleArc,
      );

      this.maxillaryOption = this.buildOption(
        "Maxillary Arc (mm)",
        this.chartData.maxillary,
        maxillaryStats,
        "#f28e2b",
        "Subject Maxillary Arc",
        this.subjectMaxillaryArc,
      );

      this.$nextTick(() => {
        this.$refs.mandibleChart?.resize();
        this.$refs.maxillaryChart?.resize();
      });
    },

    buildOption(yLabel, data, stats, lineColor, subjectName, subjectValue) {
      const fitX = data.fit_curve_x || data.ages || [];
      const rawAges = data.raw_ages || [];
      const rawValues = data.raw_values || [];
      const fit = data.fit || [];
      const p10 = data.p10 || [];
      const p90 = data.p90 || [];

      const rawSeries = this.alignedSeries(rawAges, rawValues);
      const fitSeries = this.alignedSeries(fitX, fit);
      const p10Series = this.alignedSeries(fitX, p10);
      const p90Series = this.alignedSeries(fitX, p90);
      const subjectSeries = [[this.patientAge, subjectValue]];

      return {
        tooltip: {
          trigger: "axis",
          backgroundColor: "#fff",
          borderColor: "red",
          borderWidth: 1,
          borderRadius: 6,
          padding: 10,
          formatter: (params) => {
            const items = (Array.isArray(params) ? params : [params]).filter(
              (p) => p.seriesName !== "Raw Data",
            );
            if (!items.length) return "";

            const subjectItem = items.find((p) => p.seriesName === subjectName);
            const otherItems = items.filter(
              (p) => p.seriesName !== subjectName,
            );

            let html = `<div>`;
            html += `<div style="margin-bottom:6px;">Age: ${Number(
              items[0].axisValue,
            ).toFixed(0)}</div>`;

            otherItems.forEach((p) => {
              const value = Array.isArray(p.value) ? p.value[1] : p.value;
              html += `<div style="font-weight:bold; color:${
                p.seriesName === "Gompertz Fit"
                  ? lineColor
                  : p.seriesName === "10th Percentile"
                  ? "green"
                  : "red"
              };">${p.seriesName}: ${Number(value).toFixed(2)}</div>`;
            });

            if (subjectItem) {
              const sv = Array.isArray(subjectItem.value)
                ? subjectItem.value[1]
                : subjectItem.value;

              html += `
                <div style="margin-top:10px; border:1px solid #000; border-radius:6px; padding:8px; background:#fff;">
                  <div style="color:#004ead; font-weight:bold;">${subjectName}: ${Number(
                sv,
              ).toFixed(2)}</div>
                  ${
                    stats.zScore !== null
                      ? `<div style="color:#004ead; font-weight:bold;">Z-score: ${stats.zScore.toFixed(
                          2,
                        )}</div>`
                      : ""
                  }
                  ${
                    stats.percentile !== null
                      ? `<div style="color:#004ead; font-weight:bold;">Percentile: ${stats.percentile.toFixed(
                          2,
                        )}%</div>`
                      : ""
                  }
                </div>
              `;
            }

            html += `</div>`;
            return html;
          },
        },
        legend: {
          top: 10,
          data: [
            subjectName,
            "Gompertz Fit",
            "10th Percentile",
            "90th Percentile",
          ],
        },
        grid: {
          left: "8%",
          right: "4%",
          top: "15%",
          bottom: "12%",
          containLabel: true,
        },
        xAxis: {
          type: "value",
          name: "Age",
          min: 5,
          max: 18,
          interval: 1,
        },
        yAxis: {
          type: "value",
          name: yLabel,
          min: 160,
          max: 320,
          interval: 20,
        },
        series: [
          {
            name: "Raw Data",
            type: "scatter",
            data: rawSeries,
            symbolSize: 7,
            itemStyle: {
              color:
                lineColor === "#4e79a7"
                  ? "rgba(78, 121, 167, 0.35)"
                  : "rgba(242, 142, 43, 0.35)",
            },
          },
          {
            name: "Gompertz Fit",
            type: "line",
            data: fitSeries,
            smooth: true,
            showSymbol: false,
            lineStyle: { color: lineColor, width: 2 },
          },
          {
            name: "10th Percentile",
            type: "line",
            data: p10Series,
            smooth: true,
            showSymbol: false,
            lineStyle: {
              color: "green",
              type: "dashed",
              width: 2,
            },
          },
          {
            name: "90th Percentile",
            type: "line",
            data: p90Series,
            smooth: true,
            showSymbol: false,
            lineStyle: {
              color: "red",
              type: "dashed",
              width: 2,
            },
            areaStyle: { color: "rgba(0, 123, 255, 0.12)" },
          },
          {
            name: subjectName,
            type: "scatter",
            data: subjectSeries,
            symbol: `image://${crossUrl}`,
            symbolSize: 15,
            clip: false,
            itemStyle: { color: lineColor },
          },
        ],
      };
    },
  },
};
</script>

<style scoped>
.chart {
  width: 90%;
  height: 500px;
  margin: 0 auto;
}
.mndmxl-layout {
  display: flex;
  align-items: center;
  gap: 10px;
}
.chart-container {
  flex: 5;
  display: flex;
  justify-content: center;
  position: relative;
}
.mandmxl-video-overlay {
  position: absolute;
  top: -65px;
  right: 10px;
  width: 140px;
  height: 140px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.mandmxl-video-overlay video {
  height: 85%;
  border-radius: 8px;
}
.maxillary-chart-wrapper {
  position: relative;
  padding-top: 20px;
}
</style>
